import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Field, Modal, useAsync, useToast } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { currentLocale, t } from '../lib/i18n';
import { ChannelChip } from './display';
import { CHANNEL_LABEL, ChannelTemplate, Channel, eur, PolicyRow, smsSegments } from './model';

export { ChannelChip, ChannelIcon, LiveBadge, NotificationDive, NotificationPill, SignalColumns, StepPill } from './display';

export function errText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.details?.[0];
    if (d) return `${d.message}${d.hint ? ` — ${d.hint}` : ''}${d.did_you_mean ? t(' (может быть, {suggest}?)', { suggest: d.did_you_mean }) : ''}`;
    return err.message;
  }
  return (err as Error).message;
}

// ---------- SMS counter ----------

export function SmsCounter({ text }: { text: string }) {
  const c = useMemo(() => smsSegments(text), [text]);
  return (
    <div className={`sms-counter${c.segments > 2 ? ' warn' : ''}`} aria-live="polite">
      <span>{c.encoding === 'gsm7' ? 'GSM-7' : 'UCS-2 (Unicode)'}</span>
      <span>
        {t('{n} симв.', { n: c.units })} · <b>{c.segments} {c.segments === 1 ? t('сегмент') : c.segments < 5 ? t('сегмента') : t('сегментов')}</b>
      </span>
      <span className="muted">{t('осталось {n} в последнем', { n: c.remaining })}</span>
      {c.encoding === 'ucs2' && <span className="muted">{t('Unicode из-за: {chars} — сегмент 70 вместо 160 знаков', { chars: c.unicode.map((u) => (u === ' ' ? '␠' : u)).join(' ') })}</span>}
    </div>
  );
}

// ---------- test send ----------

type Target = 'self' | 'phone' | 'contact';

/**
 * «Тестовая отправка» (§5.1): from a template and a policy to the member's own email, a phone or a contact. A policy
 * of kind verify starts a test verification instead; its code can be checked here, so the cascade visibly stops
 * on «код использован». Counted in spend; 10 per hour per workspace.
 */
export function TestSendModal({ onClose, policy: policyIn, template: templateIn, onSent }: { onClose: () => void; policy?: string; template?: string; onSent?: (id: string) => void }) {
  const { me } = useSession();
  const toast = useToast();
  const policies = useAsync(() => api.get<{ items: PolicyRow[]; builtins: PolicyRow[] }>('/cascade-policies'));
  const templates = useAsync(() => api.get<{ items: ChannelTemplate[] }>('/channel-templates'));
  const [policy, setPolicy] = useState(policyIn ?? '');
  const [template, setTemplate] = useState(templateIn ?? '');
  const [target, setTarget] = useState<Target>('phone');
  const [phone, setPhone] = useState('');
  const [contact, setContact] = useState('');
  const [data, setData] = useState('{}');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ id: string; verification?: string; estimate?: string } | null>(null);
  const [code, setCode] = useState('');
  const all = [...(policies.data?.items ?? []), ...(policies.data?.builtins ?? [])];
  const chosen = all.find((p) => p.key === policy);
  const verify = chosen?.definition.kind === 'verify';
  const tpl = templates.data?.items.find((t) => t.key === template);
  useEffect(() => {
    if (!policy && all.length) setPolicy(all.find((p) => p.definition.kind === 'message')?.key ?? all[0].key);
  }, [all.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!template && templates.data?.items.length) setTemplate(templates.data.items[0].key);
  }, [templates.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tpl && data === '{}' && tpl.variables.length) setData(JSON.stringify(Object.fromEntries(tpl.variables.map((v) => [v, /url|link/i.test(v) ? 'https://example.com/test' : /order/i.test(v) ? 'A-1042' : t('тест {v}', { v })])), null, 2));
  }, [tpl?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const to = target === 'self' ? { email: me?.user.email } : target === 'phone' ? { phone: phone.trim() } : { contact_id: contact.trim() };
  async function send() {
    setBusy(true);
    try {
      if (verify) {
        const r = await api.post<{ id: string; notification_id: string; estimated_cost_max: { amount: string } }>('/verifications/test', { to, policy });
        setResult({ id: r.notification_id, verification: r.id, estimate: r.estimated_cost_max?.amount });
        onSent?.(r.notification_id);
      } else {
        let parsed: Record<string, unknown> = {};
        try {
          parsed = JSON.parse(data || '{}');
        } catch {
          toast(t('Данные: JSON не разбирается'), 'bad');
          return;
        }
        const r = await api.post<{ id: string; estimated_cost_max: { amount: string } }>('/notifications/test', { to, policy, template, data: parsed });
        setResult({ id: r.id, estimate: r.estimated_cost_max?.amount });
        onSent?.(r.id);
      }
    } catch (err) {
      toast(errText(err), 'bad');
    } finally {
      setBusy(false);
    }
  }
  async function check() {
    if (!result?.verification) return;
    try {
      const r = await api.post<{ status: string }>(`/verifications/${result.verification}/check`, { code: code.trim() });
      toast(r.status === 'approved' ? t('Код верный: каскад остановлен, следующие шаги отменены') : t('Статус: {status}', { status: r.status }));
    } catch (err) {
      const e = err as ApiError;
      const left = (e.details as unknown as { attempts_left?: number } | undefined)?.attempts_left;
      toast(e.code === 'CODE_INVALID' ? (left != null ? t('Неверный код, осталось попыток: {n}', { n: left }) : t('Неверный код')) : e.code === 'VERIFICATION_NOT_PENDING' ? t('Верификация уже не ждёт кода (подтверждена, истекла или попытки кончились)') : errText(err), 'bad');
    }
  }
  const ready = policy && (verify || template) && (target === 'self' || (target === 'phone' ? phone.trim().length > 5 : contact.trim().startsWith('con_')));
  return (
    <Modal
      title={verify ? t('Тестовый код подтверждения') : t('Тестовая отправка')}
      onClose={onClose}
      footer={
        result ? (
          <>
            <button className="btn" onClick={onClose}>{t('Закрыть')}</button>
            <Link className="btn btn-primary" to={`/notifications/${result.id}`} onClick={onClose}>{t('Смотреть погружение')}</Link>
          </>
        ) : (
          <>
            <button className="btn" onClick={onClose}>{t('Отмена')}</button>
            <button className="btn btn-primary" disabled={!ready || busy} onClick={send}>{busy ? t('Отправляю…') : verify ? t('Отправить код') : t('Отправить')}</button>
          </>
        )
      }
    >
      {!result ? (
        <>
          <Field label={t('Политика каскада')}>
            <select className="select input" value={policy} onChange={(e) => setPolicy(e.target.value)}>
              {all.map((p) => <option key={p.key} value={p.key}>{p.key}{p.builtin && !p.version ? t(' (встроенная)') : ` v${p.version}`} · {p.definition.kind === 'verify' ? t('код') : t('сообщение')}</option>)}
            </select>
          </Field>
          {!verify && (
            <Field label={t('Шаблон канала')} hint={tpl ? t('Варианты: {list}', { list: tpl.channels.map((c) => t(CHANNEL_LABEL[c])).join(', ') }) : t('Создайте шаблон в «Каналы → Шаблоны»')}>
              <select className="select input" value={template} onChange={(e) => setTemplate(e.target.value)}>
                {(templates.data?.items ?? []).map((t) => <option key={t.key} value={t.key}>{t.name} · {t.key} v{t.version}</option>)}
              </select>
            </Field>
          )}
          <Field label={t('Получатель')}>
            <div className="chips">
              <button type="button" className={`chip${target === 'phone' ? ' on' : ''}`} onClick={() => setTarget('phone')}>{t('Телефон')}</button>
              <button type="button" className={`chip${target === 'contact' ? ' on' : ''}`} onClick={() => setTarget('contact')}>{t('Контакт')}</button>
              <button type="button" className={`chip${target === 'self' ? ' on' : ''}`} onClick={() => setTarget('self')}>{t('Мой email')}</button>
            </div>
          </Field>
          {target === 'phone' && (
            <Field label={t('Номер в формате E.164')} hint={t('С тестовым поставщиком исход решает последняя цифра: 1 — отказ, 2 — неизвестно, 3 — отказ после приёма, 6 — без отчёта, иначе — доставлено')}>
              <input className="input input-mono" inputMode="tel" placeholder="+37120000000" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
          )}
          {target === 'contact' && (
            <Field label={t('Id контакта')} hint={t('Шаги пойдут по адресам контакта: телефон, Telegram, push, web push')}>
              <input className="input input-mono" placeholder="con_…" value={contact} onChange={(e) => setContact(e.target.value)} />
            </Field>
          )}
          {target === 'self' && <div className="small muted">{t('Только email-шаги дойдут: на ваш адрес {email}. Остальные каналы пропустятся — у вас как участника нет в них адреса.', { email: me?.user.email ?? '' })}</div>}
          {!verify && (
            <Field label={t('Данные шаблона (JSON)')}>
              <textarea className="input input-mono" rows={4} value={data} onChange={(e) => setData(e.target.value)} spellCheck={false} />
            </Field>
          )}
          <div className="callout warn small"><div>{t('Тестовая отправка — настоящая: платные шаги учитываются в расходах. Не больше 10 в час на пространство.')}</div></div>
        </>
      ) : (
        <>
          <div className="callout ok"><div>{t('Уведомление')} <code>{result.id}</code> {t('создано. Худший случай по цене:')} <b>{eur(result.estimate)}</b>.</div></div>
          {result.verification && (
            <Field label={t('Введите код из сообщения')} htmlFor="ch-test-code" hint={t('Верный код останавливает каскад: шаги, ещё не переданные поставщику, отменяются')}>
              <div className="ch-inline">
                <input id="ch-test-code" className="input input-mono" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" style={{ maxWidth: 160 }} />
                <button className="btn" disabled={!code.trim()} onClick={check}>{t('Проверить код')}</button>
              </div>
            </Field>
          )}
        </>
      )}
    </Modal>
  );
}

// ---------- the fake phone (development only) ----------

interface FakeItem {
  at: string;
  channel: Channel;
  step_id: string;
  attempt: number;
  to: string;
  outcome_digit: string;
  content: { title?: string; text?: string; html?: string; url?: string; code?: string; segments?: number; encoding?: string; whatsapp_template?: { name: string } };
}

/** What the fake provider received for this workspace (outside production only; the server says 404 otherwise). */
export function FakePhone({ refreshMs = 2000 }: { refreshMs?: number }) {
  const [items, setItems] = useState<FakeItem[] | null>(null);
  const [off, setOff] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get<{ items: FakeItem[] }>('/channels/fake-inbox')
        .then((r) => alive && setItems(r.items))
        .catch(() => alive && setOff(true));
    void load();
    const t = setInterval(load, refreshMs);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [refreshMs]);
  if (off) return null;
  return (
    <div className="card fake-phone">
      <div className="card-head">
        <h3>{t('Тестовый телефон')}</h3>
        <span className="small muted">{t('только вне production')}</span>
      </div>
      <div className="card-pad">
        {!items?.length ? (
          <div className="small muted">{t('Здесь появятся сообщения, которые получил бы человек от тестового поставщика.')}</div>
        ) : (
          <ul className="fake-list">
            {items.slice(0, 12).map((m, i) => (
              <li key={`${m.step_id}-${m.attempt}-${i}`}>
                <div className="row" style={{ gap: 6 }}>
                  <ChannelChip channel={m.channel} />
                  <span className="small muted">{m.to}</span>
                  <span className="spacer" />
                  <span className="small muted">{new Date(m.at).toLocaleTimeString(currentLocale())}</span>
                </div>
                {m.content.title && <b className="small">{m.content.title}</b>}
                <div className="fake-text">{m.content.text ?? (m.content.html ? m.content.html.replace(/<[^>]+>/g, '') : m.content.whatsapp_template ? t('шаблон Meta «{name}»', { name: m.content.whatsapp_template.name }) : '—')}</div>
                {m.content.code && <div className="small">{t('код:')} <code className="fake-code">{m.content.code}</code></div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
